import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import '../../styles/Admin.scss';
import ConfirmModal from '../../components/common/ConfirmModal';

import iconDelete from '../../assets/icon_delete.svg';
import AppSpinner from '../../components/AppSpinner';

import bigFiveData from '../../assets/result-text-ko.json';

interface BigFiveResultData {
  _id: string;
  user: {
    _id: string;
    displayName: string;
    userId: string;
    email: string;
  } | null;
  results: {
    [key: string]: number;
  };
  createdAt: string;
}

const AdminBigFiveResultDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [result, setResult] = useState<BigFiveResultData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !id) return;

    const fetchResult = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`/api/admin/big-five-results/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setResult(response.data);
      } catch (err) {
        console.error('Failed to fetch Big Five result:', err);
        setError('검사 결과를 불러오는 데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchResult();
  }, [isAuthenticated, id]);

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/admin/big-five-results/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      navigate('/admin/big-five-results');
    } catch (err) {
      console.error('Failed to delete result:', err);
      alert('삭제에 실패했습니다.');
    }
  };

  const getTraitInfo = (domain: string) => {
    return bigFiveData.find(d => d.domain === domain);
  };

  const getScoreLevel = (score: number) => {
    if (score <= 45) return 'low';
    if (score >= 56) return 'high';
    return 'neutral';
  };

  const getLevelText = (score: number, traitData: any) => {
    const level = getScoreLevel(score);
    const resultText = traitData.results.find((r: any) => r.score === level);
    return resultText ? resultText.text : '';
  };

  const getLevelLabel = (score: number) => {
    if (score <= 45) return '낮음';
    if (score >= 56) return '높음';
    return '보통';
  };

  if (loading) return <AppSpinner fullscreen tip="Loading..." />;
  if (error) return <div className="admin-page">에러: {error}</div>;
  if (!result) return <div className="admin-page">검사 결과를 찾을 수 없습니다.</div>;

  return (
    <div className="admin-page">
      <h1>Big Five 검사 상세 결과</h1>

      <div className="detail-table-container">
        <table className="detail-table">
          <colgroup>
            <col style={{ width: '150px' }} />
            <col />
            <col style={{ width: '150px' }} />
            <col />
          </colgroup>
          <tbody>
            <tr>
              <th>사용자 이름</th>
              <td>{result.user?.displayName}</td>
              <th>아이디</th>
              <td>{result.user?.userId || '-'}</td>
            </tr>
            <tr>
              <th>이메일</th>
              <td>{result.user?.email}</td>
              <th>검사 일시</th>
              <td>
                {new Date(result.createdAt).toLocaleDateString()}{' '}
                {new Date(result.createdAt).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="big-five-details" style={{ marginTop: '30px' }}>
        <h3 className="mb-24">상세 분석</h3>
        {['O', 'C', 'E', 'A', 'N'].map(domain => {
          const score = result.results[domain] || 0;
          const traitInfo = getTraitInfo(domain);
          if (!traitInfo) return null;

          return (
            <div
              key={domain}
              className="trait-card"
              style={{
                marginBottom: '24px',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                padding: '20px',
              }}
            >
              <div className="trait-card-header"
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '10px',
                }}
              >
                <h4 style={{ margin: 0, fontSize: '1.1rem', color: '#3947a7' }}>
                  {traitInfo.title} ({domain})
                </h4>
                <div className="num" style={{ fontWeight: 'bold' }}>
                  <div><span style={{ fontSize: '1.2rem', color: '#333' }}>{score}</span>점</div>
                  <span
                    className={`status-badge ${getScoreLevel(score)}`}
                    style={{ marginLeft: '10px' }}
                  >
                    {getLevelLabel(score)}
                  </span>
                </div>
              </div>

              <p style={{ margin: '0 0 15px 0', color: '#666', lineHeight: '1.5' }}>
                {traitInfo.shortDescription}
              </p>

              <div
                style={{
                  background: '#f9f9f9',
                  padding: '15px',
                  borderRadius: '4px',
                  marginBottom: '15px',
                }}
              >
                <strong>결과 해석:</strong> {getLevelText(score, traitInfo)}
              </div>

              <details>
                <summary style={{ cursor: 'pointer', color: '#555', fontWeight: 600 }}>
                  요인별 상세 설명 보기
                </summary>
                <div
                  style={{ marginTop: '10px', paddingLeft: '10px', borderLeft: '3px solid #eee' }}
                >
                  <p style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem', color: '#444' }}>
                    {traitInfo.description}
                  </p>

                  <h5 style={{ marginTop: '15px', marginBottom: '5px' }}>하위 요인 (Facets)</h5>
                  <ul style={{ listStyle: 'none', padding: 0 }}>
                    {traitInfo.facets.map((facet: any) => (
                      <li key={facet.facet} style={{ marginBottom: '8px', fontSize: '0.9rem' }}>
                        <strong>{facet.title}:</strong> {facet.text}
                      </li>
                    ))}
                  </ul>
                </div>
              </details>
            </div>
          );
        })}
      </div>

      <div className="bottom-actions right-align">
        <button className="btn-list" onClick={() => navigate('/admin/big-five-results')}>
          ≡ 목록
        </button>
        <div className="action-buttons">
          <button className="btn-delete" onClick={() => setIsDeleteModalOpen(true)}>
            <img src={iconDelete} alt="delete" /> 결과 삭제
          </button>
        </div>
      </div>

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        title="결과 삭제"
        message="이 검사 결과를 삭제하시겠습니까?"
        confirmText="삭제"
        onConfirm={handleDelete}
        onCancel={() => setIsDeleteModalOpen(false)}
      />
    </div>
  );
};

export default AdminBigFiveResultDetailPage;
